import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from '@mui/material';
import { FaExclamationTriangle } from 'react-icons/fa';
import {
  DISCREPANCY_RUN_STATUS_LABELS,
  DISCREPANCY_RUN_STATUS_COLORS,
} from '../../../constants/discrepancyMappingStatuses';
import {
  RunRow, TableWrapper, TwoLine, TwoLinePrimary, TwoLineSecondary,
} from './styles';
import { describeRunTrigger, runTriggerStyle } from '../../../utils/runTriggers';
import { getChipSx } from './chipStyles';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const computePctMatch = (run) => {
  if (!run || !run.totalCount || run.totalCount === 0) return null;
  const pct = (run.matchCount / run.totalCount) * 100;
  return Math.round(pct * 10) / 10;
};

const RunsTable = ({ runs, onRowClick }) => (
  <TableWrapper>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Source</TableCell>
            <TableCell>Started</TableCell>
            <TableCell>Finished</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell align="right">Matches</TableCell>
            <TableCell align="right">Mismatches</TableCell>
            <TableCell align="right">% match</TableCell>
            {/* ADR-130: population checks, separate from the representative KPIs */}
            <TableCell align="right">
              <Tooltip title="Active people in the Regions this source owns who were not in its feed on this run" arrow>
                <span>Radar only</span>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((run) => {
            const pct = computePctMatch(run);
            return (
              <RunRow key={run.id} onClick={() => onRowClick && onRowClick(run)}>
                <TableCell>
                  {/* ADR-134: how the run started — muted when scheduled, amber when manual */}
                  <TwoLine>
                    <TwoLinePrimary>{run.sourceSystem}</TwoLinePrimary>
                    <TwoLineSecondary style={runTriggerStyle(run)}>{describeRunTrigger(run)}</TwoLineSecondary>
                  </TwoLine>
                </TableCell>
                <TableCell>{formatDateTime(run.startedAt)}</TableCell>
                <TableCell>{formatDateTime(run.finishedAt)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={DISCREPANCY_RUN_STATUS_LABELS[run.status] || run.status}
                    sx={getChipSx(DISCREPANCY_RUN_STATUS_COLORS[run.status])}
                  />
                </TableCell>
                <TableCell align="right">{run.totalCount ?? 0}</TableCell>
                <TableCell align="right">{run.matchCount ?? 0}</TableCell>
                <TableCell align="right">{run.mismatchCount ?? 0}</TableCell>
                <TableCell align="right">{pct !== null ? `${pct}%` : '—'}</TableCell>
                <TableCell align="right">
                  {run.radarOnlyCount ?? 0}
                  {run.unscopedActiveCount > 0 && (
                    <Tooltip
                      arrow
                      title={`${run.unscopedActiveCount} active people are in no Region with a system of record (no Region, a non-Region cluster, or an undecided Region), so no run checks them.`}
                    >
                      <span style={{ marginLeft: 6, color: '#B45309', verticalAlign: 'middle' }}>
                        <FaExclamationTriangle size={12} />
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </RunRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </TableWrapper>
);

export default RunsTable;
